import { useState, useEffect, useMemo, useCallback } from "react";
import { getAccessToken } from "../services/tokenHelper";
import { authService } from "../services/authService";
import { useUser } from "./useUser";

interface Permission {
  ownerId: string;
  albumId: string;
  role: "read_only" | "upload";
}

interface JWTPayload {
  id?: string;
  permissions?: Permission[];
  [key: string]: unknown;
}

/**
 * Decodes a JWT token payload (without verification)
 * Note: This is for client-side permission checking only.
 * The backend must still enforce all permissions.
 */
function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (base64url)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
}

/**
 * Hook to check album permissions for the current user
 * Returns whether the user has read-only or upload permission for a specific album
 */
export function useAlbumPermissions(ownerId: string | undefined, albumId: string | undefined) {
  const { user } = useUser();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [tokenUserId, setTokenUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      setIsLoading(true);
      try {
        // Check if user is anonymous
        const anonymous = authService.isAnonymousUser();
        setIsAnonymous(anonymous);

        const token = await getAccessToken();
        if (!token) {
          setPermissions([]);
          setTokenUserId(null);
          setIsLoading(false);
          return;
        }

        const payload = decodeJWT(token);
        if (payload?.permissions && Array.isArray(payload.permissions)) {
          setPermissions(payload.permissions);
        } else {
          setPermissions([]);
        }
        setTokenUserId(typeof payload?.id === "string" ? payload.id : null);
      } catch (error) {
        console.error("Error loading permissions:", error);
        setPermissions([]);
        setTokenUserId(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadPermissions();
  }, []);

  const albumPermission = useMemo(() => {
    if (!ownerId || !albumId) {
      return null;
    }

    // Find permission that matches the album
    // Prioritize "upload" over "read_only" if both exist

    // Check for exact upload match first (highest priority)
    const exactUploadMatch = permissions.find(
      (p) => p.ownerId === ownerId && p.albumId === albumId && p.role === "upload"
    );
    if (exactUploadMatch) {
      return exactUploadMatch;
    }

    // Check for wildcard upload match
    const wildcardUploadMatch = permissions.find(
      (p) => p.ownerId === ownerId && p.albumId === ".*" && p.role === "upload"
    );
    if (wildcardUploadMatch) {
      return wildcardUploadMatch;
    }

    // Fall back to readonly exact match
    const exactReadOnlyMatch = permissions.find(
      (p) => p.ownerId === ownerId && p.albumId === albumId && p.role === "read_only"
    );
    if (exactReadOnlyMatch) {
      return exactReadOnlyMatch;
    }

    // Fall back to wildcard readonly match (lowest priority)
    const wildcardReadOnlyMatch = permissions.find(
      (p) => p.ownerId === ownerId && p.albumId === ".*" && p.role === "read_only"
    );
    if (wildcardReadOnlyMatch) {
      return wildcardReadOnlyMatch;
    }

    return null;
  }, [permissions, ownerId, albumId]);

  const hasUploadPermission = useMemo(() => {
    return albumPermission?.role === "upload";
  }, [albumPermission]);

  const hasReadOnlyPermission = useMemo(() => {
    return albumPermission?.role === "read_only";
  }, [albumPermission]);

  const canUpload = useMemo(() => {
    return hasUploadPermission;
  }, [hasUploadPermission]);

  const currentUserId = user?.id || tokenUserId || null;

  const isAlbumOwner = useMemo(() => {
    return !!ownerId && !!currentUserId && currentUserId === ownerId;
  }, [ownerId, currentUserId]);

  const canDelete = useMemo(() => {
    // Album owner can delete any media; upload collaborators (including anonymous)
    // can delete the items they uploaded.
    return isAlbumOwner || hasUploadPermission;
  }, [isAlbumOwner, hasUploadPermission]);

  const canDeleteMedia = useCallback(
    (media: { userId: string }) => {
      if (isAlbumOwner) {
        return true;
      }
      return hasUploadPermission && !!currentUserId && !!media.userId && media.userId === currentUserId;
    },
    [isAlbumOwner, hasUploadPermission, currentUserId],
  );

  const canComment = useMemo(() => {
    // Anonymous users cannot comment
    // Logged-in users can comment if they have any permission
    return !isAnonymous && albumPermission !== null;
  }, [isAnonymous, albumPermission]);

  const canLike = useMemo(() => {
    // Anonymous users cannot like
    // Logged-in users can like if they have any permission
    return !isAnonymous && albumPermission !== null;
  }, [isAnonymous, albumPermission]);

  return {
    permissions,
    albumPermission,
    hasUploadPermission,
    hasReadOnlyPermission,
    canUpload,
    isAlbumOwner,
    canDelete,
    canDeleteMedia,
    canComment,
    canLike,
    isAnonymous,
    isLoading,
  };
}
